import { SortingState } from "@tanstack/react-table";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import {
  fetchUsersType,
  updateStatusInUsersTypes,
  updateUsersTypes,
  UsersTypesxportsCSV,
} from "slices/userstypes/thunk";
import AddUserTypeModal from "./AddUserTypeModal";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { app_modules } from "helpers/apps_modules_list";
import { toast } from "react-toastify";
import UsersTypeTableContainer from "./UsersTypeTable";
import { UserTypeConstant } from "Constants/UserTypeConstant";

const UsersTypeList = () => {
  document.title = "User Type List";
  const dispatch = useDispatch<any>();

  const selectorganisation = createSelector(
    (state: any) => state.usersType, // ✅ use lowercase to match your store setup
    (userTypesState) => ({
      organisation: userTypesState?.usersTypesList ?? [],
      rowCount: userTypesState?.rowCount ?? 0,
      loading: userTypesState?.loading ?? false,
      error: userTypesState?.error ?? null,
    })
  );
  const { organisation, rowCount, loading, error } =
    useSelector(selectorganisation);

  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.data?.permissions || user?.permissions || [];
  const master_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.master?.app_name
  );
  const user_types_permissions = master_permissions?.modules_permissions?.find(
    (item: any) =>
      item?.modules_name === app_modules?.user_management?.modules?.userType
  );

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState<any>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [colValue, setColValue] = useState<any>();
  const [showModal, setShowModal] = useState(false);
  const [filterOptions, setFilterOptions] = useState<Record<string, any[]>>({});
  const [editData, setEditData] = useState<any>({});
  const [globalFilter, setGlobalFilter] = useState("");
  const [tableInstance, setTableInstance] = useState<any>();
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      fetchUsersType({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: columnFilters,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    columnFilters,
    colValue,
    sortField,
    sortDirection,
  ]);

  // Reset to first page on filter, search, or page size change
  useEffect(() => {
    setPageIndex(0);
  }, [columnFilters, pageSize, search]);

  // Debounced search
  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
      setPageIndex(0);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  // Fetch data when search, filters, sorting, etc. change
  useEffect(() => {
    dispatch(
      fetchUsersType({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search, // <-- use debounced search value
        filters: columnFilters,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search, // <-- only use debounced search here
    columnFilters,
    colValue,
    sortField,
    sortDirection,
  ]);


  // Handle status toggle
  const handleStatusToggle = async (usertype: any) => {
    try {
      const updatedStatus = usertype.is_active === true ? false : true;
      const update_payload = { ...usertype, is_active: updatedStatus };
      await dispatch(updateStatusInUsersTypes(update_payload))
        .unwrap()
        .then(() => {
          toast.success(
            `UserType ${
              updatedStatus ? "activated" : "deactivated"
            } successfully`
          );
        });

      dispatch(
        fetchUsersType({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: columnFilters,
        })
      );
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleAddUserType = async (userType: string) => {
    try {
      await dispatch(updateUsersTypes({ user_type: userType })).unwrap();
      toast.success("User Type added successfully");
      // Re-fetch data
      dispatch(
        fetchUsersType({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: columnFilters,
        })
      );
    } catch (err) {
      toast.error("Failed to add User Type");
    }
  };

  const handleEdit = (rowData: any) => {
    setEditData(rowData); // ✅ Pass data to modal
    setShowModal(true); // Open modal in EDIT mode
  };

  const handleCreate = () => {
    setEditData(null); // ✅ Clear previous editData
    setShowModal(true); // Open modal in CREATE mode
  };
  const handleClose = () => {
    setShowModal(false);
    setEditData(null); // ✅ Clear edit data on close
  };
  // const handleExport = async () => {
  //   const resultAction = await dispatch(exportetchUsersTypes())
  //   if (exportetchUsersTypes.fulfilled.match(resultAction)) {
  //     const { blob, filename } = resultAction.payload
  //     const url = window.URL.createObjectURL(new Blob([blob]))
  //     const link = document.createElement('a')
  //     link.href = url
  //     link.setAttribute('download', filename)
  //     document.body.appendChild(link)
  //     link.click()
  //     link.parentNode?.removeChild(link)
  //    window.URL.revokeObjectURL(url)
  //   toast.success('CSV exported successfully')
  //   } else {
  //     // Optionally show error
  //     alert('Export failed')
  //   }
  // }

  const handleExport = async () => {
    try {
      const { blob, filename } = await UsersTypesxportsCSV({
        search,
        filters: columnFilters,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported successfully");
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  const DynamicAction =
    user_types_permissions?.permissions?.Update === true
      ? [
          {
            header: "Action",
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }: { row: any }) => (
              <ul className="d-flex gap-2 list-unstyled mb-0">
                <li>
                  <span
                    onClick={() => handleEdit(row?.original)}
                    className="btn btn-subtle-secondary btn-icon btn-sm edit-item-btn"
                  >
                    <i className="ph-pencil"></i>
                  </span>
                </li>
              </ul>
            ),
          },
        ]
      : [];
  // Table columns
  const columns = useMemo(
    () => [
      {
        header: " User Type",
        accessorKey: "user_type",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
      },

      {
        header: "Status",
        accessorKey: "is_active",
        enableSorting: false, // Disables sorting
        enableColumnFilter: false, // Disables filtering
        cell: ({ row }: { row: any }) => {
          const org = row.original;
          return (
            <div
              style={{ fontSize: "1.5rem", cursor: "pointer" }}
              
              onClick={() => UserTypeConstant.includes(org.user_type.toLowerCase()) && handleStatusToggle(org)}
            >
              <i
                className={`bi ${
                  org.is_active
                    ? "bi-toggle-on text-success"
                    : "bi-toggle-off text-danger"
                }`}
              />
            </div>
          );
        },
      },

      {
        header: "Created Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user ?? "N/A";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      {
        header: "Updated Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user ?? "N/A";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      ...DynamicAction,
      // {
      //   header: 'Action',
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }: { row: any }) => (
      //     <ul className='d-flex gap-2 list-unstyled mb-0'>
      //       <li>
      //         <span
      //           onClick={() => handleEdit(row?.original)}
      //           className='btn btn-subtle-secondary btn-icon btn-sm edit-item-btn'
      //         >
      //           <i className='ph-pencil'></i>
      //         </span>
      //       </li>
      //     </ul>
      //   )
      // }
    ],
    [
      dispatch,
      organisation,
      handleStatusToggle,
      user_types_permissions?.permissions,
    ]
  );

  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">Users Type List</h6>
                    </Col>
                    <Row className="align-items-center justify-content-between mt-4 g-3 flex-wrap">
                      {/* Search Box */}
                      <Col xs={12} md={6} lg={4} xl={5}>
                        <div className="search-box">
                          <Form.Control
                            type="text"
                            className="search"
                            placeholder="Search"
                            value={searchInput}
                            onChange={(e: any) =>
                              setSearchInput(e.target.value)
                            }
                          />
                          <i className="ri-search-line search-icon"></i>
                        </div>
                      </Col>

                      {/* Column Visibility + Actions */}
                      <Col xs={12} md={6} lg={8} xl={6} className="">
                        <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 w-100">
                          {/* Left side: Column Visibility */}
                          <div className="">
                            {tableInstance && (
                              <ColumnVisibilityDropdown
                                columns={tableInstance.getAllLeafColumns()}
                                excludedColumns={["action", "sr_no"]}
                              />
                            )}
                          </div>
                          <Col className="col-md-auto">
                            <div className="hstack gap-2">
                              <Button
                                className="btn btn-subtle-danger d-none"
                                id="remove-actions"
                              >
                                <i className="ri-delete-bin-2-line"></i>
                              </Button>
                              {user_types_permissions?.permissions?.View ===
                                true && (
                                <div
                                  className="hstack gap-2 btn btn-light"
                                  onClick={handleExport}
                                  style={{ cursor: "pointer" }}
                                >
                                  <i className="bi bi-box-arrow-up align-baseline me-1"></i>{" "}
                                  Export
                                </div>
                              )}
                              {user_types_permissions?.permissions?.Create ===
                                true && (
                                <Button
                                  variant="primary"
                                  onClick={() => setShowModal(true)}
                                >
                                  Create User Type
                                </Button>
                              )}
                            </div>
                          </Col>
                        </div>
                      </Col>
                    </Row>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  {loading ? (
                    <div className="text-center py-4">
                      <h5>Loading...</h5>
                    </div>
                  ) : (
                    <UsersTypeTableContainer
                      columns={columns}
                      data={
                        organisation && organisation.length > 0
                          ? organisation
                          : []
                      }
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      onPageChange={setPageIndex}
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      colValue={setColValue}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      manualSorting={true}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      //filterOptions={filterOptions}
                      SearchPlaceholder="Search organisation..."
                      onTableReady={setTableInstance}
                    />
                  )}
                  {/* <AddUserTypeModal
                    show={showModal}
                    handleClose={() => setShowModal(false)}
                    onSuccess={() =>
                      dispatch(fetchUsersType({ page: 1, pageSize: 10 }))
                    }
                    editData={editData}
                  /> */}

                  <AddUserTypeModal
                    key={editData?.user_type_id || "create"} // forces fresh instance
                    show={showModal}
                    handleClose={handleClose}
                    onSuccess={() =>
                      dispatch(fetchUsersType({ page: 1, pageSize: 10 }))
                    }
                    editData={editData}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
};

export default UsersTypeList;
